import * as React from 'react';
import { Table } from 'react-bootstrap';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import * as TimeUtils from '@acceligentllc/shared/utils/time';
import * as TimeOptionUtil from '@acceligentllc/shared/utils/timeOption';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import type { RootState } from 'af-reducers';

import type { NOTIFY_EMPLOYEE_TYPE } from 'af-constants/values';

import { Button } from '@acceligentllc/storybook';

import CustomModal from 'af-components/CustomModal';
import EmployeeList from 'af-components/NotifyModal/EmployeeList';
import TableHeader from 'af-components/NotifyModal/TableHeader';
import SubmitButton from 'af-components/SubmitButton';

import type { EmployeeNotifyModalState } from 'af-models/employeeNotifyModal.model';

import * as ScheduleBoardActions from 'af-actions/scheduleBoard';

interface NotifyEmployeeModalStateProps {
	employees: EmployeeNotifyModalState[];
	submitDisabled: boolean;
	emailNumber: number;
	smsNumber: number;
}

interface OwnProps {
	/** MM-DD-YYYY */
	dueDate: string;
	autoNotifyAt: Nullable<number>;
	autoNotifyUnassignedEmployees: boolean;
	closeModal: () => void;
	sendNotification: (dueDate: string, notifyByEmail: number[], notifyBySms: number[]) => void;
	scheduleAutoNotify: (dueDate: string, notifyByEmail: number[], notifyBySms: number[]) => void;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const NotifyParticipantsModal: React.FC<Props> = (props) => {
	const {
		dueDate,
		autoNotifyAt,
		autoNotifyUnassignedEmployees,
		checkAllInAvailableEmployeeModal,
		checkEmployeeInAvailableEmployeeModal,
		scheduleAutoNotify,
		resetAllInAvailableEmployeeModal,
		refreshAvailableEmployeesNotificationModal,
		sendNotification,
		closeModal,
		modalState,
	} = props;

	const [isSending, setIsSending] = React.useState(false);

	const {
		employees,
		submitDisabled,
		emailNumber,
		smsNumber,
	} = React.useMemo(() => {
		return modalState.employeeIds.reduce<NotifyEmployeeModalStateProps>((_acc, _empId) => {
			const _emp = modalState.employees[_empId];
			_acc.employees.push(_emp);

			if (_emp.shouldSendEmail || _emp.shouldSendSms) {
				_acc.submitDisabled = false;
			}

			if (_emp.shouldSendEmail) {
				_acc.emailNumber += 1;
			}

			if (_emp.shouldSendSms) {
				_acc.smsNumber += 1;
			}

			return _acc;
		}, { employees: [], submitDisabled: true, emailNumber: 0, smsNumber: 0 });
	}, [modalState.employeeIds, modalState.employees]);

	const resetState = React.useCallback(() => {
		resetAllInAvailableEmployeeModal(dueDate);
		setIsSending(false);
	}, [dueDate, resetAllInAvailableEmployeeModal]);

	React.useEffect(() => {
		if (modalState.isOpen) {
			refreshAvailableEmployeesNotificationModal();
			setIsSending(false);
			resetState();
		}
	}, [modalState.isOpen, refreshAvailableEmployeesNotificationModal, resetState]);

	const onCancel = React.useCallback(() => {
		resetState();
		closeModal();
	}, [closeModal, resetState]);

	const onSubmit = React.useCallback(() => {
		setIsSending(true);
		const sendEmailToIds = employees.filter((_e) => _e.shouldSendEmail).map((_e) => _e.id);
		const sendSMSToIds = employees.filter((_e) => _e.shouldSendSms).map((_e) => _e.id);
		sendNotification(dueDate, sendEmailToIds, sendSMSToIds);
		onCancel();
	}, [dueDate, employees, onCancel, sendNotification]);

	const onSchedule = React.useCallback(() => {
		setIsSending(true);
		const sendEmailToIds = employees.filter((_e) => _e.shouldSendEmail).map((_e) => _e.id);
		const sendSMSToIds = employees.filter((_e) => _e.shouldSendSms).map((_e) => _e.id);
		scheduleAutoNotify(dueDate, sendEmailToIds, sendSMSToIds);
		onCancel();
	}, [dueDate, employees, onCancel, scheduleAutoNotify]);

	const checkAll = React.useCallback((value: boolean, type: NOTIFY_EMPLOYEE_TYPE) => {
		checkAllInAvailableEmployeeModal({ value, type });

	}, [checkAllInAvailableEmployeeModal]);

	const onCheck = React.useCallback((employeeId: number, type: NOTIFY_EMPLOYEE_TYPE) => {
		checkEmployeeInAvailableEmployeeModal({ employeeId, type });
	}, [checkEmployeeInAvailableEmployeeModal]);

	const subtitle = `Notify unassigned available employees on ${dueDate}`;
	const sendLabel = modalState.hasAlreadyNotifiedEmployees ? 'Resend' : 'Send';
	const dateBefore = TimeUtils.parseMoment(dueDate, TimeFormat.DATE_ONLY)?.subtract(1, 'day')?.format(TimeFormat.DATE_ONLY_SHORT_YEAR);

	return (
		<CustomModal
			closeModal={onCancel}
			modalStyle="info"
			showModal={modalState.isOpen}
			size="xl"
		>
			<CustomModal.Header
				closeModal={onCancel}
				title={subtitle}
			/>
			<CustomModal.Body>
				<Table>
					<TableHeader checkAll={checkAll} showSubscribedColumns={!autoNotifyUnassignedEmployees} />
					<tbody>
						<EmployeeList
							employees={employees}
							isSending={isSending}
							onCheck={onCheck}
							showSubscribedColumns={!autoNotifyUnassignedEmployees}
							subscribedAt={`${dateBefore}, ${TimeOptionUtil.format(autoNotifyAt)}`}
						/>
					</tbody>
				</Table>
				<div className="question">
					Send <b><span className="text-orange">{emailNumber}</span></b> e-mail notifications
					and <b><span className="text-orange">{smsNumber}</span></b> SMS notifications to free laborers?
				</div>
				{autoNotifyUnassignedEmployees
					? <div>Automatic notifications will be sent at {TimeOptionUtil.format(autoNotifyAt)}, {dateBefore}</div>
					: <></>
				}
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button
					context="notify"
					label="Close"
					onClick={onCancel}
					style="secondary"
				/>
				<SubmitButton
					disabled={submitDisabled || isSending}
					label={isSending ? 'Sending' : sendLabel}
					onClick={onSubmit}
				/>
				{!autoNotifyUnassignedEmployees &&
					<SubmitButton
						disabled={submitDisabled || isSending}
						label={`Notify at ${dateBefore}, ${TimeOptionUtil.format(autoNotifyAt)}`}
						onClick={onSchedule}
					/>}
			</CustomModal.Footer>
		</CustomModal>
	);
};

function mapStateToProps(state: RootState, ownProps: OwnProps) {
	const { workOrdersByDateDictionary, availableEmployeesNotificationModal } = state.scheduleBoard;
	const { dueDate } = ownProps;
	const { availableNotificationStatuses } = workOrdersByDateDictionary[dueDate];

	return {
		participantsEmailAndSmsStatus: availableNotificationStatuses,
		modalState: availableEmployeesNotificationModal,
	};
}

function mapDispatchToProps() {
	return {
		resetAllInAvailableEmployeeModal: ScheduleBoardActions.resetAllInAvailableEmployeeModal,
		checkAllInAvailableEmployeeModal: ScheduleBoardActions.checkAllInAvailableEmployeeModal,
		checkEmployeeInAvailableEmployeeModal: ScheduleBoardActions.checkEmployeeInAvailableEmployeeModal,
		refreshAvailableEmployeesNotificationModal: ScheduleBoardActions.refreshAvailableEmployeesNotificationModal,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(NotifyParticipantsModal);
